import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";
import { FocusButton } from "@/components/admin/FocusDialog";

const field = "min-h-[42px] w-full border border-[#C69A3B]/60 bg-white px-2 text-sm";
const EMPTY = {
  title_es: "", title_en: "", theme_es: "", theme_en: "", year: "",
  photo_count: "", format: "print", price: "", notes_es: "", notes_en: "",
};

export default function AdminCollectionAlbums() {
  const { t } = useI18n();
  const [albums, setAlbums] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const fileRefs = useRef({});

  const load = useCallback(() => {
    api.get("/admin/collection-albums").then((r) => setAlbums(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const create = async () => {
    if (!draft.title_es.trim()) {
      toast.error(t("admin.collectionTitleRequired"));
      return;
    }
    try {
      await api.post("/admin/collection-albums", {
        ...draft,
        year: draft.year ? Number(draft.year) : null,
        photo_count: draft.photo_count ? Number(draft.photo_count) : 0,
        price: draft.price ? Number(draft.price) : 0,
      });
      setDraft(EMPTY);
      toast.success(t("admin.siteSaved"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const save = async (album, updates) => {
    try {
      await api.patch(`/admin/collection-albums/${album.id}`, updates);
      toast.success(t("admin.siteSaved"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (album) => {
    if (!window.confirm(`${album.title_es}\n\n${t("admin.collectionDeleteConfirm")}`)) return;
    try {
      await api.delete(`/admin/collection-albums/${album.id}`);
      toast.success(t("admin.siteSaved"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const uploadCover = async (album, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/site-images", form);
      await save(album, { cover_image: data.url });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-collection-panel">
      <p className="text-sm text-[#5A5752]">{t("admin.collectionHelp")}</p>

      <div className="border border-[#C69A3B]/40 bg-white p-6">
        <p className="label-mono">{t("admin.collectionNew")}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["title_es", "Título — Español"],
            ["title_en", "Title — English"],
            ["theme_es", "Tema — Español"],
            ["theme_en", "Theme — English"],
            ["year", "Año"],
            ["photo_count", "Cantidad de fotos"],
            ["price", "Precio (USD)"],
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="label-mono">{label}</span>
              <input
                className={field}
                data-testid={`collection-new-${key}`}
                value={draft[key]}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
              />
            </label>
          ))}
          <label className="block">
            <span className="label-mono">{t("admin.collectionFormat")}</span>
            <select
              className={field}
              data-testid="collection-new-format"
              value={draft.format}
              onChange={(e) => setDraft((d) => ({ ...d, format: e.target.value }))}
            >
              <option value="print">{t("collection.formatPrint")}</option>
              <option value="digital">{t("collection.formatDigital")}</option>
              <option value="both">{t("collection.formatBoth")}</option>
            </select>
          </label>
        </div>
        <div className="mt-4">
          <GoldButton onClick={create} data-testid="collection-new-btn">
            <Plus size={14} /> {t("admin.collectionAdd")}
          </GoldButton>
        </div>
      </div>

      <div className="space-y-5" data-testid="admin-collection-list">
        {albums.length === 0 && (
          <p className="text-sm text-[#5A5752]" data-testid="admin-collection-empty">
            {t("collection.empty")}
          </p>
        )}
        {albums.map((album) => (
          <div
            key={album.id}
            data-testid={`admin-collection-${album.id}`}
            className="flex flex-wrap items-start gap-4 border border-[#C69A3B]/40 bg-white p-5"
          >
            <button
              type="button"
              data-testid={`collection-cover-${album.id}`}
              onClick={() => fileRefs.current[album.id]?.click()}
              className="h-24 w-32 shrink-0 overflow-hidden border border-[#C69A3B]/50 bg-[#EFEAE0]"
            >
              {album.cover_image ? (
                <img src={album.cover_image} alt="" className="h-full w-full object-cover" />
              ) : (
                <Upload size={16} className="mx-auto text-[#C69A3B]" />
              )}
            </button>
            <input
              ref={(el) => (fileRefs.current[album.id] = el)}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid={`collection-cover-input-${album.id}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadCover(album, file);
                e.target.value = "";
              }}
            />
            <div className="grid min-w-[260px] flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["title_es", "Título — Español"],
                ["title_en", "Title — English"],
                ["theme_es", "Tema — Español"],
                ["year", "Año"],
                ["photo_count", "Fotos"],
                ["price", "Precio (USD)"],
              ].map(([key, label]) => (
                <label key={key} className="block">
                  <span className="label-mono">{label}</span>
                  <input
                    className={field}
                    data-testid={`collection-${key}-${album.id}`}
                    defaultValue={album[key] ?? ""}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const value = ["year", "photo_count", "price"].includes(key)
                        ? (raw === "" ? null : Number(raw))
                        : raw;
                      if (String(album[key] ?? "") !== String(raw)) save(album, { [key]: value });
                    }}
                  />
                </label>
              ))}
              <label className="block">
                <span className="label-mono">{t("admin.collectionFormat")}</span>
                <select
                  className={field}
                  data-testid={`collection-format-${album.id}`}
                  value={album.format || "print"}
                  onChange={(e) => save(album, { format: e.target.value })}
                >
                  <option value="print">{t("collection.formatPrint")}</option>
                  <option value="digital">{t("collection.formatDigital")}</option>
                  <option value="both">{t("collection.formatBoth")}</option>
                </select>
              </label>
              <label className="flex items-end gap-2 text-xs">
                <input
                  type="checkbox"
                  data-testid={`collection-available-${album.id}`}
                  checked={album.available !== false}
                  onChange={(e) => save(album, { available: e.target.checked })}
                />
                {t("admin.collectionAvailable")}
              </label>
            </div>
            <div className="flex flex-col gap-2">
              {album.cover_image && (
                <FocusButton url={album.cover_image} aspect="aspect-[4/3]" testId={`collection-focus-${album.id}`} />
              )}
              <button
                type="button"
                data-testid={`collection-delete-${album.id}`}
                onClick={() => remove(album)}
                className="inline-flex min-h-[42px] items-center gap-2 border border-[#8F3329] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
              >
                <Trash2 size={13} /> {t("btn.delete")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
